import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import { useBulkDownloadAndZip, useProcedureTimeRestrictions } from '../../../../hooks';
import {
  FHAntdDateTimePicker,
  FHButton,
  FHTooltip,
  FHSelect,
  FHConfirmDialog,
  FCheckbox,
} from '../../../base';
import StatusIconRenderer from '../../StatusIconRenderer';
import { clientActions, groupParentSupervisionActions } from '../../../../store';
import {
  BackgroundTypes,
  ENTITY_PREFIX,
  Roles,
  SupervisionNoteStatus,
} from '../../../../constants';
import {
  areUserRolesIn,
  disabledDatesOutsideRange,
  disabledTimeOutsideRange,
  formatEntityId,
  formatSecondsToHHMM3,
  history,
} from '../../../../helpers';
import {
  FORTA_BLACK,
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { getUtcOffset } from '../../../../utils/common';
import { ServiceCodes } from '../../billings/claims/constants';
import AttentionIcon from '../../../../assets/images/attention.svg';
import BcbaIcon from '../../../../assets/images/bcba.svg';
import DownloadButton from '../../../svgs/DownloadButton';
import {
  GROUP_SN_DURATION_OPTIONS,
  SUPERVISION_SETTING_V2,
  SUPERVISION_SETTINGS,
} from '../../supervision-notes/constants';
import TrashIcon from '../../../svgs/TrashIcon3';
import { checkInvalidFamilyAttendance } from './utils';
import AdminBackdateForm from './AdminBackdateForm';

import type { IFamilyAttendance, SelectValueType } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { Moment } from 'moment';

const tooltipStyle = {
  '& .MuiTooltip-tooltip': {
    borderRadius: '8px',
    boxShadow: '0px 0px 6px 0px rgba(18, 78, 120, 0.25)',
    color: FORTA_GRAY4,
    marginTop: -5,
    fontWeight: 700,

    '& .MuiTooltip-arrow:before': {
      boxShadow: '0px 0px 6px 0px rgba(18, 78, 120, 0.25)',
    },
  },
};

interface ControlProps {
  isEditable: boolean;
}

const TooltipTitle = ({
  startBetween10to12,
  endBetween10to12,
}: {
  startBetween10to12: boolean;
  endBetween10to12: boolean;
}) => (
  <Box data-testid="tooltipTitle" sx={{ display: 'flex', alignItems: 'center' }}>
    <img
      style={{
        width: '20px',
        height: '20px',
        marginRight: '8px',
        marginTop: '-4px',
        filter: 'brightness(84%) contrast(189%) hue-rotate(145deg) saturate(300%)',
      }}
      src={AttentionIcon}
      alt="attention-icon"
    />
    {startBetween10to12 || endBetween10to12 ? (
      <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
        Attention: Supervisions conducted between{' '}
        <b>
          <span style={{ color: FORTA_ERROR }}>10:00 p.m and 12:00 a.m </span> are unlikely to be
          accepted by the insurance.
        </b>
      </Typography>
    ) : (
      <Typography sx={{ fontSize: '16px', color: FORTA_BLACK }}>
        Attention: Supervisions conducted between{' '}
        <b>
          <span style={{ color: FORTA_ERROR }}>12:00 a.m and 4:00 a.m </span> will not be accepted
          by the insurance.
        </b>
      </Typography>
    )}
  </Box>
);

interface IDurationChangeReviewContentProps {
  currentDurationVal: number;
  keepFullAttendances: boolean;
  attendances: IFamilyAttendance[];
  setKeepFullAttendances: (val: boolean) => void;
}

function DurationChangeReviewContent({
  currentDurationVal,
  keepFullAttendances,
  attendances,
  setKeepFullAttendances,
}: IDurationChangeReviewContentProps) {
  const fullAttendances = attendances.filter((a) => a.duration === currentDurationVal);
  const partialAttendaces = attendances.filter((a) => a.duration !== currentDurationVal);
  return (
    <Box mb={3}>
      <Typography color={FORTA_GRAY4} fontSize={14}>
        This may affect family attendance.
      </Typography>
      <Typography color={FORTA_GRAY4} fontSize={14}>
        Please review the following before submitting.
      </Typography>
      {fullAttendances.length > 0 && (
        <Box my={2}>
          <Typography fontWeight={700} mb={0.5}>
            Families currently marked as fully attending.
          </Typography>
          <Box>
            <ul style={{ marginLeft: '-26px' }}>
              {fullAttendances.map((a) => (
                <li key={a.client.id}>
                  <Typography
                    fontSize={14}
                    fontWeight={700}
                    color={FORTA_BLUE.primary}
                  >{`${a.client.first_name} ${a.client.last_name}`}</Typography>
                </li>
              ))}
            </ul>
            <Box mt={-1}>
              <Typography fontSize={14} fontWeight={700} mb={0.2}>
                Do you want to keep these families marked as fully attending?
              </Typography>
              <FCheckbox
                label={'Yes, keep them marked as fully attending'}
                checked={keepFullAttendances}
                coloredBackground
                iconSize={{ width: 16, height: 16 }}
                onChange={(e) => {
                  setKeepFullAttendances(e.target.checked);
                }}
                sx={{
                  width: 'fit-content',
                  fontSize: 14,
                  '& .MuiSvgIcon-root': { fontSize: 16 },
                  '& .MuiFormControlLabel-label': {
                    fontSize: 14,
                    color: FORTA_GRAY4,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
      {partialAttendaces.length > 0 && (
        <Box mt={4}>
          <Typography fontWeight={700}>Families currently marked as partially attending</Typography>
          <Box mb={1}>
            <Typography fontSize={14} color={FORTA_GRAY4} mb={0.5}>
              Partial attendance must be updated <strong>manually</strong> after this change.
            </Typography>
            <ul style={{ marginLeft: '-26px' }}>
              {partialAttendaces.map((a) => (
                <li key={a.client.id}>
                  <Typography
                    fontSize={14}
                    fontWeight={700}
                    color={FORTA_BLUE.primary}
                  >{`${a.client.first_name} ${a.client.last_name}`}</Typography>
                </li>
              ))}
            </ul>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Controls({ isEditable }: ControlProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);

  const {
    current: groupParentSN,
    loading: groupSNLoading,
    pdfDownloadUrls,
  } = useSelector((state: RootState) => state.groupParentSupervision);

  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [duration, setDuration] = useState<number | string>('');
  const [durationTempVal, setDurationTempVal] = useState<number | null>(null);
  const [startDateTempVal, setStartDateTempVal] = useState<Moment | null>(null);
  const [keepFullAttendances, setKeepFullAttendances] = useState<boolean>(true);
  const [openDateReviewModal, setOpenDateReviewModal] = useState<boolean>(false);
  const [openDurationRevierModal, setOpenDurationRevierModal] = useState<boolean>(false);
  const [setting, setSetting] = useState<string | null>(null);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [showConfirmMakeNoteEditableDialog, setShowConfirmMakeNoteEditableDialog] =
    useState<boolean>(false);
  const [showConfirmBackdateNoteDialog, setShowConfirmBackdateNoteDialog] =
    useState<boolean>(false);
  const [backdatedDate, setBackdatedDate] = useState<Moment | null>(null);

  const { timeToolTipOpen, durationToolTipOpen, startBetween10to12, endBetween10to12 } =
    useProcedureTimeRestrictions(startDate, Number(duration), !isEditable);

  const [showDeleteButton, containsInvalidFamilyAttendance] = useMemo(() => {
    const showDeleteButton = Boolean(
      groupParentSN?.id &&
      groupParentSN?.status !== SupervisionNoteStatus.Completed &&
      (groupParentSN.bcba.email === user?.email || areUserRolesIn(user?.roles, [Roles.Admin])),
    );

    const containsInvalidFamilyAttendance = checkInvalidFamilyAttendance(
      groupParentSN?.duration ?? 0,
      groupParentSN?.date_start ?? '',
      groupParentSN?.family_attendance ?? [],
    );

    return [showDeleteButton, containsInvalidFamilyAttendance];
  }, [groupParentSN, user]);

  const showButtonToMakeNoteEditable = useMemo(() => {
    return (
      areUserRolesIn(user?.roles, [Roles.Admin]) &&
      groupParentSN?.id &&
      groupParentSN.status === SupervisionNoteStatus.Completed
    );
  }, [groupParentSN, user]);

  const showFormToBackdateNote = useMemo(() => {
    return (
      areUserRolesIn(user?.roles, [Roles.Admin]) &&
      groupParentSN?.id &&
      (groupParentSN.status === SupervisionNoteStatus.Editing ||
        groupParentSN.status === SupervisionNoteStatus.Started)
    );
  }, [groupParentSN, user]);

  useBulkDownloadAndZip(
    pdfDownloadUrls,
    `${formatEntityId(String(groupParentSN?.id), ENTITY_PREFIX.GroupSupervisionNotes)}_PDFs`,
    true,
  );

  useEffect(() => {
    if (!groupParentSN) {
      setStartDate(null);
      setDuration('');
      setSetting(SUPERVISION_SETTINGS[0].value);
      return;
    }
    setStartDate(moment(groupParentSN.date_start));
    setBackdatedDate(moment(groupParentSN.date_start));
    setDuration(groupParentSN.duration);
    setSetting(groupParentSN.setting);
  }, [groupParentSN]);

  const handleStartNotes = async () => {
    if (!startDate || !duration || !setting) return;
    const data = {
      date_start: startDate.toISOString(),
      utc_offset_date_start: getUtcOffset(moment(new Date())),
      duration,
      setting,
    };
    const response = await dispatch(
      groupParentSupervisionActions.startGroupSupervision({ getAccessTokenSilently, data }),
    );
    if (response.payload && response.payload.id) {
      history.navigate(`/group-parent-training/${response.payload.id}/`);
    }
  };

  const handleDurationUpdate = (value: number) => {
    setDurationTempVal(value);
    setOpenDurationRevierModal(true);
  };

  const handleDateUpdate = (value: Moment) => {
    setStartDateTempVal(value);
    setOpenDateReviewModal(true);
  };

  const handleSaveDuration = async (startDate: Moment | null, setting: string) => {
    if (!groupParentSN || !startDate || !setting) return;

    const data = {
      date_start: (startDateTempVal ?? startDate).toISOString(),
      utc_offset_date_start: getUtcOffset(moment(new Date())),
      duration: durationTempVal ?? duration,
      setting,
    };

    const attendances = groupParentSN.family_attendance;
    const familyAttendanceTimeMap: { [key: string]: { startDiff: number; endDiff: number } } = {};
    const familiesWithFullAttendace = attendances.filter((a) => a.duration === duration);
    familiesWithFullAttendace.forEach((a) => {
      const startDiff = moment(a.date_start).diff(moment(groupParentSN.date_start), 'seconds');
      const endDiff = moment(groupParentSN.date_start)
        .add(groupParentSN.duration, 'seconds')
        .diff(a.date_end, 'seconds');
      familyAttendanceTimeMap[`${a.id}`] = {
        startDiff,
        endDiff,
      };
    });

    const result = await dispatch(
      groupParentSupervisionActions.updateGroupSupervision({
        groupSNId: groupParentSN.id,
        getAccessTokenSilently,
        data,
      }),
    );
    if (result?.payload && result?.payload?.date_start) {
      if (keepFullAttendances) {
        const patchFamilyPromiseArray: Array<Promise<any>> = [];
        familiesWithFullAttendace.forEach((a) => {
          const diff = familyAttendanceTimeMap[`${a.id}`];
          const data = {
            response: a.response,
            date_start: moment(result?.payload?.date_start)
              .add(diff.startDiff, 'seconds')
              .toISOString(),
            date_end: moment(result?.payload?.date_start)
              .add(result?.payload?.duration, 'seconds')
              .subtract(diff.endDiff, 'seconds')
              .toISOString(),
          };
          const promise = dispatch(
            groupParentSupervisionActions.updateFamilyAttendance({
              getAccessTokenSilently,
              groupSNId: groupParentSN.id,
              attendanceId: a.id,
              data,
            }),
          );
          patchFamilyPromiseArray.push(promise);
        });
        await Promise.all(patchFamilyPromiseArray);
      }
      dispatch(
        clientActions.getClientsFamily({
          getAccessTokenSilently,
          dateActive: result?.payload?.date_start,
        }),
      );
      setDuration(result?.payload?.duration);
      setStartDate(moment(result?.payload?.date_start));
      setKeepFullAttendances(true);
      setOpenDateReviewModal(false);
      setOpenDurationRevierModal(false);
      setDurationTempVal(null);
    }
  };

  const handleUpdateNotes = async (duration: number, startDate: Moment | null, setting: string) => {
    if (!groupParentSN || !startDate || !duration || !setting) return;

    const data = {
      date_start: startDate.toISOString(),
      utc_offset_date_start: getUtcOffset(moment(new Date())),
      duration,
      setting,
    };

    const result = await dispatch(
      groupParentSupervisionActions.updateGroupSupervision({
        groupSNId: groupParentSN.id,
        getAccessTokenSilently,
        data,
      }),
    );
    if (result?.payload && result?.payload?.date_start) {
      dispatch(
        clientActions.getClientsFamily({
          getAccessTokenSilently,
          dateActive: result?.payload?.date_start,
        }),
      );
    }
  };

  const handleDownloadPDFs = () => {
    dispatch(
      groupParentSupervisionActions.getGroupSupervisionDownloadUrls({
        getAccessTokenSilently,
        groupSNId: groupParentSN?.id,
      }),
    );
  };

  const handleSelectDateTime = (value: Moment) => {
    if (!value) return;
    if (containsInvalidFamilyAttendance) {
      toast.error('Please correct family attendance before changing the session time.');
      return;
    }
    let val = value?.seconds(0).milliseconds(0);
    const now = moment();

    if (value.isAfter(now)) {
      val = moment(); // Set value to current time, if value is in future.
    }
    if (!groupParentSN?.id) {
      setStartDate(val);
    } else {
      setStartDateTempVal(val);
    }
  };

  const handleDeleteGpTraining = async () => {
    if (!groupParentSN?.id) return;
    setShowConfirmDeleteDialog(false);

    const { error } = await dispatch(
      groupParentSupervisionActions.deleteGroupSupervision({
        groupSNId: groupParentSN.id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    history.navigate('/dashboard');
  };

  const handleMakeGroupSupervisionEditable = async () => {
    if (!groupParentSN?.id) return;
    setShowConfirmMakeNoteEditableDialog(false);

    const { error } = await dispatch(
      groupParentSupervisionActions.makeGroupSupervisionEditable({
        groupSNId: groupParentSN.id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;

    await dispatch(
      groupParentSupervisionActions.getGroupSupervision({
        getAccessTokenSilently,
        groupSNId: groupParentSN.id,
      }),
    );
  };

  const handleBackdateGroupSupervision = async () => {
    if (!groupParentSN?.id || !backdatedDate) return;
    setShowConfirmBackdateNoteDialog(false);

    const { error } = await dispatch(
      groupParentSupervisionActions.backdateGroupSupervision({
        groupSNId: groupParentSN.id,
        data: {
          date_start: backdatedDate,
          utc_offset_date_start: getUtcOffset(backdatedDate),
        },
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    await dispatch(
      groupParentSupervisionActions.getGroupSupervision({
        getAccessTokenSilently,
        groupSNId: groupParentSN.id,
      }),
    );
  };

  return isEditable ? (
    <Grid container spacing={3}>
      <AdminBackdateForm
        visible={!!showFormToBackdateNote}
        backdatedDate={backdatedDate}
        onBackdatedDateChange={setBackdatedDate}
        onBackdateClick={() => setShowConfirmBackdateNoteDialog(true)}
      />
      <Grid item xs={12} sm={6} md={3} lg={3} mt={2}>
        <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
          Start date and time
        </Typography>
        <FHTooltip
          sx={{ inset: 'auto auto -6px 0px' }}
          title={
            <TooltipTitle
              startBetween10to12={startBetween10to12}
              endBetween10to12={endBetween10to12}
            />
          }
          arrow
          open={timeToolTipOpen || startBetween10to12}
          placement="top"
          maxwidth={400}
          tooltipBorderRadius="8px"
          tooltipbackground={FORTA_WHITE}
          tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
        >
          <Box>
            <FHAntdDateTimePicker
              data-testid="startDate"
              format="MM/DD/YYYY h:mm a"
              showTime={{ format: 'h:mm a' }}
              sx={{
                width: '100%',
                boxShadow: 'none',
              }}
              allowClear={false}
              showNow={true}
              fullWidth
              style={{ flexGrow: 1 }}
              inputHeight={40}
              use12Hours
              coloredBackground
              disabled={!isEditable || groupSNLoading}
              value={startDate ?? ''}
              disabledDate={(current: Moment) => disabledDatesOutsideRange(current, -3, 0)}
              disabledTime={(current: Moment) => disabledTimeOutsideRange(current, 72)}
              onChange={handleSelectDateTime}
              onSelect={handleSelectDateTime}
              onBlur={(e: any) => {
                if (!groupParentSN?.id) return;
                if (containsInvalidFamilyAttendance) {
                  return;
                }
                // Do not update if value is same as before
                if (moment(startDate).isSame(moment(e.target.value), 'minute')) return;
                if (groupParentSN?.id && !groupParentSN?.family_attendance?.length) {
                  handleUpdateNotes(
                    Number(duration),
                    moment(e.target.value)?.seconds(0).milliseconds(0),
                    setting ?? '',
                  );
                  return;
                }
                handleDateUpdate(moment(e.target.value)?.seconds(0).milliseconds(0));
              }}
              onOk={(newValue: Moment) => {
                if (!groupParentSN?.id) return;
                if (containsInvalidFamilyAttendance) {
                  toast.error('Please correct family attendance before changing the session time.');
                  return;
                }
                // Do not update if value is same as before
                if (moment(startDate).isSame(moment(newValue), 'minute')) return;
                if (groupParentSN?.id && !groupParentSN?.family_attendance?.length) {
                  handleUpdateNotes(
                    Number(duration),
                    newValue?.seconds(0).milliseconds(0),
                    setting ?? '',
                  );
                  return;
                }
                handleDateUpdate(newValue?.seconds(0).milliseconds(0));
              }}
              error={timeToolTipOpen || startBetween10to12}
            />
          </Box>
        </FHTooltip>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3} mt={2}>
        <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
          Duration
        </Typography>
        <FHTooltip
          sx={{ inset: 'auto auto -6px 0px' }}
          title={
            <TooltipTitle
              startBetween10to12={startBetween10to12}
              endBetween10to12={endBetween10to12}
            />
          }
          arrow
          open={durationToolTipOpen || endBetween10to12}
          placement="top"
          maxwidth={400}
          tooltipBorderRadius="8px"
          tooltipbackground={FORTA_WHITE}
          tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
        >
          <Box>
            <FHSelect
              data-testid="duration"
              sx={{ flexGrow: 1, width: '100%', boxShadow: 'none' }}
              inputHeight={40}
              menuProps={{ maxHeight: '500px' }}
              backgroundType={BackgroundTypes.Colored}
              disabled={!isEditable || groupSNLoading}
              value={duration}
              options={GROUP_SN_DURATION_OPTIONS}
              optionDataTestIdPrefix="duration_option"
              onUpdate={(value: SelectValueType) => {
                if (!groupParentSN?.id) {
                  setDuration(value);
                  return;
                }
                if (containsInvalidFamilyAttendance) {
                  toast.error('Please correct family attendance before updating session duration.');
                  return;
                }
                if (!groupParentSN?.family_attendance?.length) {
                  setDuration(value);
                  handleUpdateNotes(Number(value), startDate, setting ?? '');
                  return;
                }
                handleDurationUpdate(Number(value));
              }}
              error={durationToolTipOpen || endBetween10to12}
            />
          </Box>
        </FHTooltip>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3} mt={2}>
        <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
          Delivery method
        </Typography>
        <Box>
          <FHSelect
            data-testid="setting"
            sx={{ flexGrow: 1, width: '100%', boxShadow: 'none' }}
            inputHeight={40}
            menuProps={{ maxHeight: '500px' }}
            backgroundType={BackgroundTypes.Colored}
            disabled={!isEditable || groupSNLoading}
            value={setting}
            options={SUPERVISION_SETTING_V2}
            optionDataTestIdPrefix="setting_option"
            onUpdate={(value: SelectValueType) => {
              setSetting(String(value));
            }}
            error={!setting}
          />
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3} mt={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 4 }}>
          {!groupParentSN?.id && (
            <FHButton
              sx={{ width: '160px' }}
              data-testid="startGroupSNButton"
              fullWidth
              disabled={
                !startDate || !duration || !setting || timeToolTipOpen || durationToolTipOpen
              }
              onClick={handleStartNotes}
            >
              Start Notes
            </FHButton>
          )}
          {showDeleteButton && (
            <IconButton
              sx={{
                p: 1,
                borderRadius: '8px',
                color: FORTA_ERROR,
                opacity: 0.6,
                border: `1px solid ${FORTA_ERROR}`,
                '&:hover': {
                  opacity: 1,
                },
              }}
              data-testid="deleteGroupSN"
              onClick={() => setShowConfirmDeleteDialog(true)}
            >
              <TrashIcon color={FORTA_ERROR} width={18} height={18} />
              <Typography ml={1}>Delete</Typography>
            </IconButton>
          )}
        </Box>
      </Grid>
      <FHConfirmDialog
        open={showConfirmDeleteDialog}
        title={'Delete group parent training'}
        content={
          'Are you sure you want to delete this group parent training? This action cannot be undone.'
        }
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        submittext={'Yes, delete'}
        canceltext={'Cancel'}
        submitBtnTestId={'deleteBtn'}
        cancelBtnTestId={'cancelBtn'}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onSubmit={handleDeleteGpTraining}
      />
      <FHConfirmDialog
        open={showConfirmBackdateNoteDialog}
        title={'Change'}
        content={
          'Are you sure you want to change the start date of this group parent training note?'
        }
        submittext={'Yes'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmBackdateNoteDialog(false)}
        onSubmit={handleBackdateGroupSupervision}
      />
      <FHConfirmDialog
        open={openDateReviewModal}
        title={'Update session time'}
        dialogHeight={'auto'}
        contentRenderer={() => (
          <DurationChangeReviewContent
            currentDurationVal={Number(duration)}
            keepFullAttendances={keepFullAttendances}
            attendances={groupParentSN?.family_attendance ?? []}
            setKeepFullAttendances={setKeepFullAttendances}
          />
        )}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        submittext={'Submit'}
        canceltext={'Cancel'}
        disableSubmitButton={false}
        onClose={() => {
          setOpenDateReviewModal(false);
          setStartDateTempVal(null);
          setKeepFullAttendances(true);
        }}
        onSubmit={() => {
          handleSaveDuration(startDate, setting ?? '');
        }}
      />
      <FHConfirmDialog
        open={openDurationRevierModal}
        title={'Update Duration'}
        dialogHeight={'auto'}
        contentRenderer={() => (
          <DurationChangeReviewContent
            currentDurationVal={Number(duration)}
            keepFullAttendances={keepFullAttendances}
            attendances={groupParentSN?.family_attendance ?? []}
            setKeepFullAttendances={setKeepFullAttendances}
          />
        )}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        submittext={'Submit'}
        canceltext={'Cancel'}
        disableSubmitButton={false}
        onClose={() => {
          setOpenDurationRevierModal(false);
          setDurationTempVal(null);
          setKeepFullAttendances(true);
        }}
        onSubmit={() => {
          handleSaveDuration(startDate, setting ?? '');
        }}
      />
    </Grid>
  ) : groupParentSN ? (
    <Grid container spacing={3} justifyContent="space-between" alignItems="center">
      <AdminBackdateForm
        visible={!!showFormToBackdateNote}
        backdatedDate={backdatedDate}
        onBackdatedDateChange={setBackdatedDate}
        onBackdateClick={() => setShowConfirmBackdateNoteDialog(true)}
      />
      <Grid item xs={12} sm={6} md={9} lg={9} mt={2} sx={{ display: 'flex' }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ mt: 0.7, mr: 2 }}>
            <StatusIconRenderer
              iconHeight={40}
              iconWidth={40}
              procedureCode={ServiceCodes.GroupSNProcedureCode}
              status={groupParentSN.status}
            />
          </Box>
          <Box textAlign="left" mr={2}>
            <Typography fontSize={14} color={FORTA_GRAY3}>
              Start date and time
            </Typography>
            <Box sx={{ display: 'flex' }}>
              <Typography>
                {groupParentSN?.date_start
                  ? `${moment(groupParentSN?.date_start).format('MM/DD/YYYY')}`
                  : ''}
              </Typography>
              <Typography ml={2}>
                {groupParentSN?.date_start
                  ? `${moment(groupParentSN?.date_start).format('hh:mm a')}`
                  : ''}
              </Typography>
            </Box>
          </Box>
          <Box textAlign="left" mx={2}>
            <Typography fontSize={14} color={FORTA_GRAY3}>
              Duration
            </Typography>
            {formatSecondsToHHMM3(groupParentSN?.duration ?? 0)}
          </Box>
          <Box textAlign="left" mx={2}>
            <Typography fontSize={14} color={FORTA_GRAY3}>
              Delivery method
            </Typography>
            {setting}
          </Box>
          <Divider orientation="vertical" sx={{ borderWidth: 1 }} />
          <Box textAlign="left" mx={2}>
            <Typography fontSize={14} color={FORTA_GRAY3}>
              BCBA
            </Typography>
            <Box sx={{ display: 'flex' }}>
              <FHTooltip title={'BCBA'} arrow sx={tooltipStyle} tooltipbackground={FORTA_WHITE}>
                <img src={BcbaIcon} alt="bcba" />
              </FHTooltip>
              <Typography ml={1}>
                {groupParentSN?.bcba
                  ? `${groupParentSN?.bcba?.first_name} ${groupParentSN?.bcba?.last_name}`
                  : ''}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} md={3} lg={3} mt={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 4 }}>
          {showButtonToMakeNoteEditable && (
            <FHButton sx={{ mr: 2 }} onClick={() => setShowConfirmMakeNoteEditableDialog(true)}>
              Edit
            </FHButton>
          )}
          {groupParentSN?.status === SupervisionNoteStatus.Completed ? (
            <IconButton sx={{ padding: 0, height: 40 }} onClick={handleDownloadPDFs}>
              <DownloadButton />
            </IconButton>
          ) : (
            <FHButton
              sx={{ width: '160px' }}
              data-testid="startGroupSNButton"
              fullWidth
              disabled={
                !startDate ||
                !duration ||
                Boolean(groupParentSN?.id) ||
                timeToolTipOpen ||
                durationToolTipOpen
              }
              onClick={handleStartNotes}
            >
              Start Notes
            </FHButton>
          )}
          {showDeleteButton && (
            <IconButton
              sx={{
                p: 1,
                ml: 2,
                opacity: 0.5,
                '&:hover': {
                  opacity: 1,
                },
              }}
              onClick={() => setShowConfirmDeleteDialog(true)}
            >
              <TrashIcon color={FORTA_BLUE.primary} width={24} height={24} />
            </IconButton>
          )}
        </Box>
      </Grid>
      <FHConfirmDialog
        open={showConfirmDeleteDialog}
        title={'Delete group parent training'}
        content={
          'Are you sure you want to delete this group parent training? This action cannot be undone.'
        }
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
        submittext={'Yes, delete'}
        canceltext={'Cancel'}
        submitBtnTestId={'deleteBtn'}
        cancelBtnTestId={'cancelBtn'}
        onClose={() => setShowConfirmDeleteDialog(false)}
        onSubmit={handleDeleteGpTraining}
      />
      <FHConfirmDialog
        open={showConfirmMakeNoteEditableDialog}
        title={'Make editable'}
        content={'Are you sure you want to make this group parent training note editable?'}
        submittext={'Yes'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmMakeNoteEditableDialog(false)}
        onSubmit={handleMakeGroupSupervisionEditable}
      />
      <FHConfirmDialog
        open={showConfirmBackdateNoteDialog}
        title={'Change'}
        content={
          'Are you sure you want to change the start date of this group parent training note?'
        }
        submittext={'Yes'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmBackdateNoteDialog(false)}
        onSubmit={handleBackdateGroupSupervision}
      />
    </Grid>
  ) : null;
}

export default Controls;
